import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, AfterViewInit } from '@angular/core';
import Keyboard from 'simple-keyboard';
import autocorrect from "simple-keyboard-autocorrect";

import { getKeyboardLayout, KeyboardTemplate } from './keyboard-templates';
import { retry } from 'rxjs/operators';

const KEYBOARD_EXPAND_DURATION = 100;

@Component({
  standalone: false,
	selector: 'app-custom-keyboard',
	templateUrl: './custom-keyboard.component.html',
	styleUrls: ['./custom-keyboard.component.scss'],
	animations: [
		trigger('collapse', [
			state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
			state('true', style({ height: '0', visibility: 'hidden' })),
			transition('true => false', animate(KEYBOARD_EXPAND_DURATION + 'ms ease-out')),
			transition('false => true', animate(KEYBOARD_EXPAND_DURATION + 'ms ease-in')),
		]),
	],
	encapsulation: ViewEncapsulation.None,
})
export class CustomKeyboardComponent implements OnInit, AfterViewInit {

	@Input()
	set template(value: KeyboardTemplate) {
		this._template = value;
	}
	get template(): KeyboardTemplate {
		return this._template;
	}

	@Input()
	set searchElements(value: string[]) {
		this._searchElements = value;
	}
	get searchElements(): string[] {
		return this._searchElements;
	}

	@Input()
	set wordList(value: string[]) {
		this._wordList = value;
	}
	get wordList(): string[] {
		return this._wordList;
	}

	@Output() public typedValue: EventEmitter<string> = new EventEmitter<string>();

	@Input() set setValue(value: string) {
		this.value = value;
		if (this.keyboard) {
			this.keyboard.setInput(this.value);
		}
	}
	public value = '';

	public keyboard: Keyboard;
	public keyboardIndex: number = Math.floor(Math.random() * 1000000);

	public isFocusInsideComponent: boolean = false;
	public isComponentClicked: boolean = false;
	public collapsed: boolean = true;

	private _searchElements: string[];
	private _wordList: string[];
	filteredSuggestions: string[];
	private _template: KeyboardTemplate = KeyboardTemplate.Standard;

	constructor() {}

	ngOnInit() {}

	ngAfterViewInit(): void {
		this._initKeyboard();
	}

	private _initKeyboard(): void {
		this.keyboard = new Keyboard('.keyboard' + this.keyboardIndex, {
			onChange: (input) => this.onChange(input),
			onKeyPress: (button) => this.onKeyPress(button),
			mergeDisplay: true,
			layoutName: 'default',
			layout: getKeyboardLayout(this._template),
			preventMouseDownDefault: true,
			autocorrectHotkey: "{space}",
			modules: [
					autocorrect
			],
			display: {
				'{numbers}': '123',
				'{ent}': '⏎ enter',
				'{escape}': 'esc ⎋',
				'{tab}': 'tab ⇥',
				'{backspace}': '←',
				'{capslock}': 'caps lock ⇪',
				'{shift}': '⇧ shift',
				'{controlleft}': 'ctrl ⌃',
				'{controlright}': 'ctrl ⌃',
				'{altleft}': 'alt ⌥',
				'{altright}': 'alt ⌥',
				'{metaleft}': 'cmd ⌘',
				'{metaright}': 'cmd ⌘',
				'{abc}': 'ABC',
				'{clear}': '🗑',
				'{space}': '⎵',
			},
		});
		this.keyboard.setInput(this.value);

		this._tuneKeyboardLook();
	}

	private _tuneKeyboardLook(): void {
		const buttonsToTune: string[] = ['.hg-button-clear', '.hg-button-backspace'];
		buttonsToTune.forEach((button) => {
			const backspaceButton = document.querySelectorAll(button);
			backspaceButton?.forEach((b) => b.setAttribute('style', 'width: 20px'));
		});
	}

	public clearValue(): void {
		this.value = '';
		this.keyboard.setInput(this.value);
		this.typedValue.emit(this.value);
		this._markButtons([], this.value);
	}

	public handleEnter(): void {
		let caretPosition = this.keyboard.getCaretPosition();
		if (caretPosition >= 0) {
			let chars = this.value.split('');
			chars.splice(caretPosition, 0, '\n');
			let valueWithEnter = chars.join('');
			this.onChange(valueWithEnter);
			this.keyboard.setCaretPosition(caretPosition + 1);
		}
	}

	public toggleKeyboard(open: boolean = false): void {
		this.collapsed = !open || !this.collapsed;
		this.value = '';
	}

	public onChange = (inputValue: string) => {
		this.value = inputValue;
		this.keyboard.setInput(this.value);
		this.typedValue.emit(this.value);
		this._markButtons(this.searchElements, inputValue);
		this.filterSuggestionsList(inputValue);
	};

	filterSuggestionsList(inputValue: string) {
		 if (inputValue == "" || this.wordList == null || this.wordList.length == 0) {
				this.filteredSuggestions = [];
				return;
			}

	  var filteredWords = this.wordList.filter(x => x.toLowerCase().includes(inputValue));

			if (filteredWords.length > 30) {
				this.filteredSuggestions = [];
				return;
			}

			this.filteredSuggestions = filteredWords;
	}

	selectSuggestedWord(suggestedWord: string) {
		this.keyboard.setInput(suggestedWord);
		this.typedValue.emit(suggestedWord);
		this.filterSuggestionsList(suggestedWord);
	}

	onKeyPress(button: string) {
		if (button === '{shift}' || button === '{lock}') this.handleShift();
		if (button === '{numbers}' || button === '{abc}') this.handleNumbers();
		if (button === '{clear}') this.clearValue();
		if (button === '{ent}') this.handleEnter();
	}

	onInputChange = (event: any) => {
		this.keyboard.setInput(event.target.value);
	};

	handleShift = () => {
		let currentLayout = this.keyboard.options.layoutName;
		let shiftToggle = currentLayout === 'default' ? 'shift' : 'default';

		this.keyboard.setOptions({
			layoutName: shiftToggle,
		});
	};

	handleNumbers() {
		let currentLayout = this.keyboard.options.layoutName;
		let numbersToggle = currentLayout !== 'numbers' ? 'numbers' : 'default';

		this.keyboard.setOptions({
			layoutName: numbersToggle,
		});
	}

	private _markButtons(filteredItems: string[], typedText: string): void {
		const buttons = document.querySelectorAll(`.keyboard${this.keyboardIndex} .hg-row .hg-button.hg-standardBtn`);
		buttons.forEach((button) => {
			if (!filteredItems?.length || !typedText) {
				button.classList.remove('no-results');
			} else if (
				!filteredItems?.find((i) =>
					i
						.replace(/<[^>]*>?/gm, '')
						.toLocaleLowerCase()
						.includes(typedText + button.getAttribute('data-skbtn'))
				)
			) {
				button.classList.add('no-results');
			} else {
				button.classList.remove('no-results');
			}
		});
	}
}
